import { RootState } from "@base/store";
import { WithAnalytics } from "@base/utils/hoc/withSendPageAnalytics";
import Button from "@components/atoms/button";
import Chip from "@components/atoms/chip";
import Divider from "@components/atoms/divider";
import FinishedGameDetailsCard from "@components/molecules/finished-game-details-card";
import GameDetailsCard from "@components/molecules/game-details-card";
import Header from "@components/molecules/header";
import Layout from "@components/molecules/layout";
import Menu from "@components/molecules/menu";
import OrganizerFinishedGameDetailsCard from "@components/molecules/organizer-finished-game-details-card";
import { LoaderSnackbarWrapperActions } from "@components/organisms/LoaderSnackbarWrapper/LoaderSnackbarWrapper.slice";
import theme from "@config/theme";
import { Box, Snackbar } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import Slide from "@mui/material/Slide";
import { TransitionProps } from "@mui/material/transitions";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import * as React from "react";
import { useEffect, useState } from "react";
import { isBrowser, isMobile } from "react-device-detect";
import { ConnectedProps, connect, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { WhatsappShareButton } from "react-share";

import { gameActions } from "../game/game.slice";
import { GameProps, PlayerProps, WaitlistProps } from "../game/game.type";
import { reviewActions } from "../review/review.slice";
import { TransactionProps } from "../transactions/transaction.type";
import { transactionsActions } from "../transactions/transactions.slice";
import { gameDetailsActions } from "./gameDetails.slice";
import {
  ActionsWrapper,
  Avatar,
  BaseButton,
  ChipWrapper,
  Content,
  DesktopShareDialog,
  DesktopShareTitle,
  OptionWithDividerWrapper,
  OptionWithIconWrapper,
  OptionWrapper,
  Options,
  OrganizerLabel,
  PlayerInfoWrapper,
  PlayerName,
  PlayerWrapper,
  PlayersContainer,
  ShareActions,
  Team,
  TeamDescription,
  TeamHeader,
  TeamMembers,
  TeamTitle,
  Teams,
  TeamsWrapper,
  Title,
  UserViewChipWrapper,
} from "./gameDetails.styled";

import { ReactComponent as DownIcon } from "@assets/images/chevrons/chevron-down.svg";
import { ReactComponent as Copy } from "@assets/images/copy/copyIcon.svg";
import { ReactComponent as Whatsapp } from "@assets/images/whatsApp/whatsapp.svg";

type ReduxProps = ConnectedProps<typeof connector>;

const { REACT_APP_DEV } = process.env;

const GameDetails = ({
  reset,
  setAlert,
  rsvpToAGame,
  setSelectedGame,
  adjustPaymentStatus,
  removePlayerFromGamePlayer,
  refundPlayer,
  userHasStripeAccount,
  movePlayerToOtherSide,
  getAllGameReview,
  getGameReviewById,
  leaveWaitList,
  getGameReviewAndAverage,
  getPaymentsIntents,
}: ReduxProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  dayjs.extend(utc);
  dayjs.extend(timezone);
  dayjs.extend(localizedFormat);
  dayjs.extend(customParseFormat);

  const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
      children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
  ) {
    return <Slide direction="up" ref={ref} {...props} />;
  });

  const selectedGame = useSelector((state: RootState) => state.selectedGame);

  const [gameStatus, setGameStatus] = useState<"upcoming" | "past" | null>(
    null
  );
  const [hasStripeAccount, setHasStripeAccount] = useState<boolean | null>(
    null
  );
  const [paymentStatus, setPaymentStatus] = useState(false);
  const [urlCopied, setIsUrlCopied] = useState<boolean>(false);
  const [isDesktopShareOpen, setIsDesktopShareOpen] = useState<boolean>(false);
  const [intentId, setIntentId] = useState<string>("");

  const handleTooltipClose = () => {
    setIsDesktopShareOpen(false);
  };

  const handleTooltipOpen = () => {
    setIsDesktopShareOpen(true);
  };

  const findWaitlistIdByUserId = (userIdToFind: string) => {
    const foundUser = selectedGame?.waitlist?.find(
      (item: WaitlistProps) => item.user.id === userIdToFind
    );

    if (foundUser) {
      return foundUser.id;
    }
  };

  const handleLeaveWaitList = async () => {
    const waitlistId = findWaitlistIdByUserId(userId!);
    if (waitlistId) {
      const response = await leaveWaitList(waitlistId);
      setSelectedGame(response?.payload);
    }
  };

  const HasUserStripeAccount = async () => {
    const response = await userHasStripeAccount();
    switch (response?.payload?.details_submitted) {
      case false:
        setHasStripeAccount(false);

        break;
      case true:
        setHasStripeAccount(true);
        break;
      case undefined:
        setHasStripeAccount(false);
    }
  };

  const isUserAPlayerInitial = (game: GameProps, id: string) => {
    let isPlayer = false;
    game.sides.forEach((side) => {
      const player = side.players.find(
        (player) => player?.rsvp?.user?.id === id
      );
      if (player) {
        isPlayer = true;
      }
    });
    return isPlayer;
  };
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [gameRate, setGameRate] = useState<null>(null);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerProps>();
  const [selectedSide, setSelectedSide] = useState<"A" | "B">();
  const userId = useSelector((state: RootState) => state.user.user.uuid);
  const open = Boolean(anchorEl);
  const [isAttending, setIsAttending] = useState<boolean>(
    isUserAPlayerInitial(selectedGame, userId!)
  );

  const handleDateTime = (inputDate: string) => {
    const formattedDate = dayjs(inputDate).format("h:mm A, DD/MM/YY");

    return formattedDate;
  };

  const hasPaid = async () => {
    const response = await getPaymentsIntents(selectedGame?.id);
    if (response?.meta?.requestStatus === "fulfilled") {
      response?.payload?.forEach((trx: TransactionProps) => {
        if (trx.status === "succeeded") {
          setPaymentStatus(true);
          setIntentId(trx?.id);
        }
      });
    }
  };

  const handleGameStatus = (): "past" | "upcoming" => {
    const currentDateTime = dayjs().utc().format("YYYY-MM-DDTHH:mm:ss[Z]");
    if (selectedGame?.end_date < currentDateTime) {
      setGameStatus("past");
      return "past";
    } else {
      setGameStatus("upcoming");
      return "upcoming";
    }
  };

  const handleOnShare = async () => {
    if (isMobile) {
      const shareData = {
        title: `${selectedGame?.title}`,
        text: `Join ${selectedGame?.title} game!
Time: ${getDate(selectedGame?.start_date)}
Location: ${selectedGame?.address?.properties?.name}, ${
          selectedGame?.address?.properties?.address
        }

RSVP on this link:\n`,
        url: `${REACT_APP_DEV}/game-details/${selectedGame?.id}`,
      };
      if (navigator.share) {
        await navigator.share(shareData);
      }
    }
    if (isBrowser) {
      handleTooltipOpen();
    }
  };

  const getDate = (date: string) => {
    return new Date(date).toLocaleString("en-US", {
      dateStyle: "full",
      timeStyle: "short",
    });
  };

  const DesktopShare = () => {
    return (
      <Dialog
        open={isDesktopShareOpen}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleTooltipClose}
        aria-describedby="alert-dialog-slide-description"
      >
        <DesktopShareDialog>
          <DesktopShareTitle>
            {"Invite your friends to this game!"}
          </DesktopShareTitle>
          <ShareActions>
            <Button
              fontFamily="NotoSans-SemiBold"
              fontSize="14px"
              textColor={"#FFF"}
              text={"Copy Link"}
              endIcon={<Copy />}
              color="secondary"
              type={"button"}
              onClick={() => {
                navigator.clipboard.writeText(
                  `Join ${selectedGame?.title} game!
Time: ${getDate(selectedGame?.start_date)}
Location: ${selectedGame?.address?.properties?.name}, ${
                    selectedGame?.address?.properties?.address
                  }

RSVP on this link:
${REACT_APP_DEV}/game-details/${selectedGame?.id}`
                );
                setIsUrlCopied(true);
              }}
            />
            <WhatsappShareButton
              style={{ width: "100%" }}
              url={`${REACT_APP_DEV}/game-details/${selectedGame?.id}`}
              title={`Join ${selectedGame?.title} game!
Time: ${getDate(selectedGame?.start_date)}
Location: ${selectedGame?.address?.properties?.name}, ${
                selectedGame?.address?.properties?.address
              }

RSVP on this link:\n`}
              separator=""
            >
              <Button
                fontFamily="NotoSans-SemiBold"
                fontSize="14px"
                textColor={"#FFF"}
                text={"Share Game via WhatsApp"}
                endIcon={<Whatsapp />}
                color="secondary"
                type={"button"}
                onClick={() => {}}
              />
            </WhatsappShareButton>
          </ShareActions>
        </DesktopShareDialog>
      </Dialog>
    );
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getGameRating = async () => {
    const response = await getGameReviewAndAverage(selectedGame?.id);
    if (response?.meta?.requestStatus === "fulfilled") {
      if (response?.payload?.reviews_count === 0) {
        setGameRate(null);
      } else {
        setGameRate(response?.payload);
      }
    }
  };

  const moveToSide = async (PlayerId: string, sideId: string) => {
    const body = { side_id: sideId };
    const response = await movePlayerToOtherSide({
      gameId: selectedGame?.id,
      PlayerId: PlayerId,
      body,
    });
    if (response?.meta?.requestStatus === "fulfilled") {
      setSelectedGame(response?.payload);
    }
  };

  const refundPlayerPayment = async (rsvp_id: string) => {
    await refundPlayer(rsvp_id);
  };

  const changePaymentStatus = async (isPaid: boolean) => {
    const body = {
      paid: isPaid,
    };
    const response = await adjustPaymentStatus({
      gameId: selectedGame?.id,
      rsvpId: selectedPlayer?.rsvp?.id!,
      body,
    });
    if (response?.meta?.requestStatus === "fulfilled") {
      setSelectedGame(response?.payload?.game);
    }
  };

  const removePlayer = async () => {
    const response = await removePlayerFromGamePlayer({
      gameId: selectedGame?.id,
      playerId: selectedPlayer?.id!,
    });
    if (response?.meta?.requestStatus === "fulfilled") {
      setSelectedGame(response?.payload?.game);
    }
  };

  const getAdmin = () => {
    const adminId = selectedGame?.game_admins[0]?.user?.id;
    return adminId;
  };

  const isOrganizer = (playerId: string) => {
    return getAdmin() === playerId;
  };

  const isUserOrganizer = () => {
    return selectedGame?.game_admins[0]?.user?.id === userId;
  };

  const PlayerPaymentsOptions = ({
    handleClose,
  }: {
    handleClose: () => void;
  }) => {
    return (
      <OptionWrapper>
        <BaseButton color={selectedPlayer?.rsvp?.paid ? "#805AD5" : "#36BBA0"}>
          {selectedPlayer?.rsvp?.paid ? (
            <Options
              onClick={() => {
                changePaymentStatus(false);
                handleClose();
              }}
            >
              Mark as unpaid
            </Options>
          ) : (
            <Options
              onClick={() => {
                changePaymentStatus(true);
                handleClose();
              }}
            >
              Mark as paid
            </Options>
          )}
        </BaseButton>
        <BaseButton color={"black"}>
          {selectedSide === "A" ? (
            <Options
              onClick={() => {
                moveToSide(selectedPlayer?.id!, selectedGame?.sides[1].id);
                handleClose();
              }}
            >
              Move to Side B
            </Options>
          ) : (
            <Options
              onClick={() => {
                moveToSide(selectedPlayer?.id!, selectedGame?.sides[0].id);
                handleClose();
              }}
            >
              Move to Side A
            </Options>
          )}
        </BaseButton>
        <BaseButton color="#D75613">
          <Options
            onClick={() => {
              removePlayer();
              handleClose();
            }}
          >
            Remove player
          </Options>
        </BaseButton>
        <BaseButton color="#D75613">
          <Options
            onClick={() => {
              refundPlayerPayment(selectedPlayer?.rsvp.id!);
              handleClose();
            }}
          >
            Refund Payment
          </Options>
        </BaseButton>
      </OptionWrapper>
    );
  };

  const organizerViewRender = (players: PlayerProps[], side: "A" | "B") => {
    return (
      <>
        {players.length > 0 ? (
          <PlayersContainer>
            {players.map((player, index) => (
              <PlayerInfoWrapper key={index}>
                <>
                  {isOrganizer(player?.rsvp?.user?.id) ? (
                    <>
                      <OptionWithDividerWrapper>
                        <OptionWithIconWrapper>
                          <PlayerWrapper>
                            <Avatar
                              alt="user Photo"
                              src={player?.rsvp?.user?.profile_url}
                              {...stringAvatar(
                                `${player?.rsvp?.user?.first_name} ${player?.rsvp?.user?.last_name}`
                              )}
                            />
                            <OrganizerLabel>{`${player?.rsvp?.user?.first_name} ${player?.rsvp?.user?.last_name} (Organizer)`}</OrganizerLabel>
                          </PlayerWrapper>
                        </OptionWithIconWrapper>
                        {index === players.length && (
                          <Divider orientation="horizontal" />
                        )}
                      </OptionWithDividerWrapper>
                    </>
                  ) : (
                    <>
                      <BaseButton
                        onClick={(e: any) => {
                          handleClick(e);
                          setSelectedPlayer(player);
                          setSelectedSide(side);
                        }}
                      >
                        <OptionWithDividerWrapper>
                          <OptionWithIconWrapper>
                            <PlayerWrapper>
                              <Avatar
                                alt="user Photo"
                                src={player?.rsvp?.user?.profile_url}
                                {...stringAvatar(
                                  `${player?.rsvp?.user?.first_name} ${player?.rsvp?.user?.last_name}`
                                )}
                              />
                              <PlayerName>{`${player?.rsvp?.user?.first_name} ${player?.rsvp?.user?.last_name}`}</PlayerName>
                            </PlayerWrapper>
                            <ChipWrapper>
                              {!isOrganizer(player?.rsvp?.user?.id) && (
                                <Chip
                                  label={player?.rsvp?.paid ? "paid" : "Unpaid"}
                                  color={
                                    player?.rsvp?.paid ? "#36BBA0" : "#8D59D1"
                                  }
                                  bgColor={
                                    player?.rsvp?.paid
                                      ? " rgba(54, 187, 160, 0.13)"
                                      : "#F0E5FF"
                                  }
                                />
                              )}
                              <DownIcon />
                            </ChipWrapper>
                          </OptionWithIconWrapper>
                          {index === players.length && (
                            <Divider orientation="horizontal" />
                          )}
                        </OptionWithDividerWrapper>
                      </BaseButton>
                      <Menu
                        anchorEl={anchorEl}
                        handleClose={handleClose}
                        open={open}
                        menuWrapper={
                          <PlayerPaymentsOptions handleClose={handleClose} />
                        }
                      />
                    </>
                  )}
                </>
              </PlayerInfoWrapper>
            ))}
          </PlayersContainer>
        ) : (
          <TeamDescription>
            {
              "No one in this team yet. Invite more players to see more people in this list."
            }
          </TeamDescription>
        )}
      </>
    );
  };

  const userViewRender = (players: PlayerProps[]) => {
    return (
      <>
        {players.length > 0 ? (
          <PlayersContainer>
            {players.map((player, index) => (
              <PlayerInfoWrapper key={index}>
                <OptionWithDividerWrapper>
                  <OptionWithIconWrapper>
                    <PlayerWrapper>
                      <Avatar
                        alt="user Photo"
                        src={player?.rsvp?.user?.profile_url}
                        {...stringAvatar(
                          `${player?.rsvp?.user?.first_name} ${player?.rsvp?.user?.last_name}`
                        )}
                      />
                      {isOrganizer(player?.rsvp?.user?.id) ? (
                        <OrganizerLabel>{`${player?.rsvp?.user?.first_name} ${player?.rsvp?.user?.last_name} (Organizer)`}</OrganizerLabel>
                      ) : (
                        <PlayerName>{`${player?.rsvp?.user?.first_name} ${player?.rsvp?.user?.last_name}`}</PlayerName>
                      )}
                    </PlayerWrapper>
                    <UserViewChipWrapper>
                      {!isOrganizer(player?.rsvp?.user?.id) && (
                        <Chip
                          label={player?.rsvp?.paid ? "paid" : "Unpaid"}
                          color={player?.rsvp?.paid ? "#36BBA0" : "#8D59D1"}
                          bgColor={
                            player?.rsvp?.paid
                              ? " rgba(54, 187, 160, 0.13)"
                              : "#F0E5FF"
                          }
                        />
                      )}
                    </UserViewChipWrapper>
                  </OptionWithIconWrapper>
                  {index === players.length && (
                    <Divider orientation="horizontal" />
                  )}
                </OptionWithDividerWrapper>
              </PlayerInfoWrapper>
            ))}
          </PlayersContainer>
        ) : (
          <TeamDescription>
            {
              "No one in this team yet. Invite more players to see more people in this list."
            }
          </TeamDescription>
        )}
      </>
    );
  };

  const waitlistUserViewRender = (players: WaitlistProps[]) => {
    return (
      <>
        {players.length > 0 ? (
          <PlayersContainer>
            {players.map((player, index) => (
              <PlayerInfoWrapper key={index}>
                <OptionWithDividerWrapper>
                  <OptionWithIconWrapper>
                    <PlayerWrapper>
                      <Avatar
                        alt="user Photo"
                        src={player?.user?.profile_url}
                        {...stringAvatar(
                          `${player?.user?.first_name} ${player?.user?.last_name}`
                        )}
                      />
                      {isOrganizer(player?.user?.id) ? (
                        <OrganizerLabel>{`${player?.user?.first_name} ${player?.user?.last_name} (Organizer)`}</OrganizerLabel>
                      ) : (
                        <PlayerName>{`${player?.user?.first_name} ${player?.user?.last_name}`}</PlayerName>
                      )}
                    </PlayerWrapper>
                  </OptionWithIconWrapper>
                  {index === players.length && (
                    <Divider orientation="horizontal" />
                  )}
                </OptionWithDividerWrapper>
              </PlayerInfoWrapper>
            ))}
          </PlayersContainer>
        ) : (
          <TeamDescription>{"No one on the waitlist yet."}</TeamDescription>
        )}
      </>
    );
  };

  const teamRender = (
    title: string,
    players: PlayerProps[],
    side: "A" | "B"
  ) => {
    return (
      <Team>
        <TeamHeader>
          <TeamTitle>{title}</TeamTitle>
          <TeamMembers>{players.length}</TeamMembers>
        </TeamHeader>
        {isUserOrganizer()
          ? organizerViewRender(players, side)
          : userViewRender(players)}
      </Team>
    );
  };

  const waitlistRender = (
    title: string,
    players: WaitlistProps[],
    side: "Waitlist"
  ) => {
    return (
      <Team>
        <TeamHeader>
          <TeamTitle>{title}</TeamTitle>
          <TeamMembers>{players.length}</TeamMembers>
        </TeamHeader>
        {waitlistUserViewRender(players)}
      </Team>
    );
  };

  const stringAvatar = (name: string) => {
    return {
      children: `${name?.split(" ")[0][0]?.toUpperCase()}${name
        ?.split(" ")[1][0]
        ?.toUpperCase()}`,
    };
  };

  const handleBack = () => {
    reset();
    navigate(location?.state?.from || "/home");
  };

  const handleGameEdit = () => {
    if (selectedGame.id) {
      navigate(`/game-edit/${selectedGame.id}`, {
        state: { from: location?.state?.from || "/home" },
      });
    }
  };

  const isUserAPlayer = (game: GameProps, id: string) => {
    let isPlayer = false;

    game.sides.forEach((side) => {
      const player = side.players.find(
        (player) => player?.rsvp?.user?.id === id
      );
      if (player) {
        isPlayer = true;
      }
    });
    setIsAttending(isPlayer);
    return isPlayer;
  };

  const isUserASub = !!selectedGame?.waitlist?.some(
    (entry: any) => entry.user.id === userId
  );

  const handleRSVP = async (status: "N" | "A") => {
    const body = {
      status,
    };
    if (selectedGame?.id && userId) {
      const response = await rsvpToAGame({ id: selectedGame?.id, body });
      if (response?.meta.requestStatus === "fulfilled") {
        isUserAPlayer(response?.payload?.game, userId);
        setSelectedGame(response?.payload?.game);
        if (!response?.payload?.success) {
          setAlert({
            alert: {
              isVisible: true,
              message: `${response?.payload?.message}`,
              type: "error",
            },
          });
        }
      }
    }
  };

  const upcomingGameDetailsRender = () => {
    const currentPLayersQty =
      selectedGame?.sides[0].players.length +
      selectedGame?.sides[1].players.length;
    return (
      <>
        <GameDetailsCard
          handelShare={() => {
            gameStatus === "upcoming" && handleOnShare();
          }}
          paymentStatus={paymentStatus}
          addressCoordinate={{
            latitude: selectedGame?.address?.properties?.coordinates?.latitude,
            longitude:
              selectedGame?.address?.properties?.coordinates?.longitude,
          }}
          selectedGame={selectedGame?.id}
          hasStripeAccount={hasStripeAccount}
          // postPaymentIntent={postPaymentIntent}
          handleLeaveWaitList={handleLeaveWaitList}
          isUserASub={isUserASub}
          gameStatus={gameStatus}
          startDate={selectedGame?.start_date}
          endDate={selectedGame?.end_date}
          maxPlayers={selectedGame?.max_players}
          currentPlayersQty={currentPLayersQty}
          isAttending={isAttending}
          handleRSVP={handleRSVP}
          currency={selectedGame?.currency}
          handleEdit={isUserOrganizer() ? handleGameEdit : undefined}
          date={handleDateTime(selectedGame?.start_date)}
          title={selectedGame?.title}
          location={
            selectedGame?.address?.properties?.name || selectedGame?.location
          }
          intentId={intentId}
          isOrganizer={isOrganizer(userId!)}
          link={selectedGame?.chat_link}
          info={selectedGame?.description}
          fee={selectedGame?.fee}
          isCash={selectedGame?.accept_cash_payment!}
          isOnlineTransfer={selectedGame?.accept_online_payment!}
          isAvailableOnMarketplace={
            isUserOrganizer()
              ? selectedGame?.available_on_marketplace!
              : undefined
          }
          isOnlinePayment={selectedGame?.accept_app_payment!}
        />
      </>
    );
  };

  const pastGameDetailsRender = () => {
    return (
      <>
        {isUserOrganizer() ? (
          <OrganizerFinishedGameDetailsCard
            currency={selectedGame?.currency}
            isAttending={isAttending}
            handleRSVP={handleRSVP}
            handleEdit={handleGameEdit}
            date={handleDateTime(selectedGame?.start_date)}
            title={selectedGame?.title}
            location={
              selectedGame?.address?.properties?.name || selectedGame?.location
            }
            link={selectedGame?.chat_link}
            info={selectedGame?.description}
            fee={selectedGame?.fee}
            gameRate={gameRate}
            sideAId={selectedGame?.sides[0].id}
            sideBId={selectedGame?.sides[1].id}
            sideATitle={selectedGame?.sides[0]?.side_name}
            sideBTitle={selectedGame?.sides[1]?.side_name}
            scoreA={selectedGame?.sides[0]?.score?.score_value}
            scoreB={selectedGame?.sides[1]?.score?.score_value}
          />
        ) : (
          <FinishedGameDetailsCard
            currency={selectedGame?.currency}
            isAttending={isAttending}
            handleRSVP={handleRSVP}
            handleEdit={handleGameEdit}
            date={handleDateTime(selectedGame?.start_date)}
            title={selectedGame?.title}
            location={
              selectedGame?.address?.properties?.name || selectedGame?.location
            }
            link={selectedGame?.chat_link}
            gameRate={gameRate}
            info={selectedGame?.description}
            fee={selectedGame?.fee}
            sideATitle={selectedGame?.sides[0]?.side_name}
            sideBTitle={selectedGame?.sides[1]?.side_name}
            scoreA={selectedGame?.sides[0]?.score?.score_value || 0}
            scoreB={selectedGame?.sides[1]?.score?.score_value || 0}
          />
        )}
      </>
    );
  };

  useEffect(() => {
    if (!selectedGame.id) {
      navigate("/home");
    }
  }, [selectedGame]);

  useEffect(() => {
    if (userId) {
      isUserAPlayer(selectedGame, userId);
    }
    if (!gameStatus) {
      handleGameStatus();
    }
    if (selectedGame?.id) {
      hasPaid();
    }
    HasUserStripeAccount();
  }, []);

  useEffect(() => {
    if (urlCopied) {
      handleTooltipClose();
    }
  }, [urlCopied]);

  useEffect(() => {
    if (gameStatus === "past") {
      getGameRating();
    }
  }, [gameStatus]);

  return (
    <Layout
      isLayout={true}
      HeaderChildren={
        <>
          <Header
            title=""
            handleBack={handleBack}
            // icon={gameStatus === "upcoming" && <Share />}
            // handleClick={() => {
            //   gameStatus === "upcoming" && handleOnShare();
            // }}
          />
        </>
      }
    >
      {
        <>
          {/* <CheckoutForm /> */}
          {selectedGame.id && (
            <>
              {gameStatus === "upcoming"
                ? upcomingGameDetailsRender()
                : pastGameDetailsRender()}
              <Content>
                <TeamsWrapper>
                  <Title>{`Players (${
                    selectedGame?.sides[0].players.length +
                    selectedGame?.sides[1].players.length
                  }/${selectedGame?.max_players})`}</Title>
                  <Teams>
                    <Box>
                      {teamRender(
                        selectedGame?.sides[0].side_name,
                        selectedGame?.sides[0].players,
                        "A"
                      )}
                    </Box>
                    <Divider orientation="horizontal" />
                    <Box>
                      {teamRender(
                        selectedGame?.sides[1].side_name,
                        selectedGame?.sides[1].players,
                        "B"
                      )}
                    </Box>
                    <Box>
                      {waitlistRender(
                        "Waitlist",
                        selectedGame?.waitlist || [],
                        "Waitlist"
                      )}
                    </Box>
                  </Teams>
                </TeamsWrapper>
                <ActionsWrapper>
                  {/* {isUserOrganizer() && (
                    <Button
                      fontFamily="Outfit-SemiBold"
                      fontSize="16px"
                      textColor={theme.palette.grey[100]}
                      text={"Edit teams"}
                      type={"button"}
                      isDisabled
                      startIcon={<DisabledPencil />}
                      onClick={() => {}}
                    />
                  )} */}
                  {gameStatus === "past" && gameRate === null && (
                    <Button
                      iscta
                      textColor={theme.palette.grey[100]}
                      text={"Leave a review"}
                      type={"button"}
                      onClick={() => {
                        navigate("/review");
                      }}
                    />
                  )}
                </ActionsWrapper>
                <Snackbar
                  open={urlCopied}
                  autoHideDuration={3000}
                  onClose={() => setIsUrlCopied(false)}
                  message="Copied To Clipboard!"
                  anchorOrigin={{ vertical: "top", horizontal: "left" }}
                />
              </Content>
              <DesktopShare />
            </>
          )}
        </>
      }
    </Layout>
  );
};
const mapDispatchToProps = {
  setSelectedGame: gameDetailsActions.setSelectedGame,
  rsvpToAGame: gameDetailsActions.rsvpToAGame,
  reset: gameDetailsActions.reset,
  getAllGameReview: reviewActions.getGameReviews,
  getGameReviewById: reviewActions.getGameReviewById,
  getGameReviewAndAverage: reviewActions.getGameReviewAndAverage,
  removePlayerFromGamePlayer: gameDetailsActions.removePlayer,
  refundPlayer: gameDetailsActions.refundPlayer,
  adjustPaymentStatus: gameDetailsActions.adjustPaymentStatus,
  getPaymentsIntents: transactionsActions.getPaymentsIntentsByGameId,
  leaveWaitList: gameActions.leaveWaitList,
  movePlayerToOtherSide: gameDetailsActions.moveToSide,
  setAlert: LoaderSnackbarWrapperActions.setGlobalAppearances,
  userHasStripeAccount: transactionsActions.userHasStripeAccount,
};

const connector = connect(undefined, mapDispatchToProps);
const GameDetailsRedux = connector(WithAnalytics(GameDetails));

export { GameDetailsRedux as GameDetails };
